import { CREATE_BOOKING_TYPE, MODALS } from "../../../config/params";
/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import { DentalIcon, DiagnosticIcon, PreventativeMedIcon, VaccinationIcon } from "../../../components/Icons";
import { IPetItemResponse, ISOCItemResponse }
  from "../../../services/Standard-of-care/standard-of-care.interface";
import React, { useEffect, useLayoutEffect, useState } from "react";

import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import NullGeneric from "../../../components/NullGeneric/NullGeneric";
import SOCAccordionCard from "./SOCAccordionCard";
import SocReminderCard from "./SocReminderCard";
import { Typography } from "@mui/material";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useBookingAppointmentTypeStore } from "../../../hooks";
import { useModal } from "../../../context/ModalContext";
import { useOverdueInvoiceStore } from "../../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { usePetGeneralCare } from "../../../services/Standard-of-care/standard-of-care.service";
import { userActivityLogger } from "../../../utils/mixpanel";

type IStandardOfCare = {
  socData: {
    [key: string]: ISOCItemResponse[];
  }
  petData: IPetItemResponse;
}

export const SOC_ICONS: { [key:string]: JSX.Element } = {
  "diagnostic": <DiagnosticIcon/>,
  "vaccination": <VaccinationIcon/>,
  "preventative": <PreventativeMedIcon/>,
  "dental": <DentalIcon/>,
};


export type ISpecificOverdueItem = {
  overdueItem?: ISOCItemResponse | null;
  parentValue?: string | null;
}

const StandardOfCareContainer = (props: IStandardOfCare) => {
  const { socData, petData } = props;
  const { setSelectedPets, setAppointmentsForm } = useAppointmentStore();
  const SOC_KEYS_DATA = Object.keys(socData);
  const { openModal } = useModal();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [reminderCard, setReminderCard] = useState<boolean>(false);
  const [isWellnessPlan, setIsWellnessPlan]=useState<boolean>(false);
  const { getGeneralCare, petCare } = usePetGeneralCare();
  const [specificOverdueItem, setSpecificOverdueItem] = useState<ISpecificOverdueItem | null>();
  const { pendingPaymentPetList, isBookingBlocked, pausedWellnessPetList, cancelledWellnessPetList } = useOverdueInvoiceStore();
  const { bookingAppointmentType } = useBookingAppointmentTypeStore();

  useLayoutEffect(()=>{
    /* here we are checking few logics
     1. If All SOC's are empty expect Preventative medication and,
     2. everySocData(i.e Diagnostic, Vaccination and Dental)-has status as "Current" except Preventative medication
     we are not checking socStatus of preventative medication */
    const socExpectPreventative = Object.keys(socData).every((key) =>
      key === "Preventative" || socData[key].length === 0 || socData[key].every((item) => item.status === "Current"),
    );

    if (petData?.socStatus !== "Current" && petData?.socStatus !== "" && !socExpectPreventative) {
      setReminderCard(true);
    } else {
      setReminderCard(false);
    }
  }, [petData]);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);

    setSpecificOverdueItem({ ...specificOverdueItem, parentValue: null });
  };

  const finalObject: any = new Map();

  SOC_KEYS_DATA.forEach((item) => {
    finalObject.set(item, socData && socData[item]);
  });

  const socMappedData: Array<[string, ISOCItemResponse[]]> = Array?.from(finalObject);

  const handleReminder = (value: boolean) => {
    setReminderCard(value);
  };

  const handleOpenSchedule = () => {
    if (!isBookingBlocked && (pendingPaymentPetList.length > 0)) {
      if (cancelledWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      } else if (pausedWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
      } else {
        openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
      }
    } else {
      openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
    }
  };

  const handleBookAppointment = () => {
    if (petData) {
      userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT, { start_from: "care_section", pet_name: petData.name,
        appt_type: bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ? "drop_off" : "in_room_visit",
        overdue_status: pendingPaymentPetList.length > 0 ? "yes" : "no",
        is_plan_cancelled: cancelledWellnessPetList.length > 0 ? "true" : "false",
      });
      userActivityLogger(MIXPANEL_EVENTS. BOOK_A_VISIT_SELECT_PET, { pet_name: petData.name });
      setAppointmentsForm(null);
      setSelectedPets([petData]);
      handleOpenSchedule();
    }
  };


  useEffect(() => {
    if (petData) {
      userActivityLogger(MIXPANEL_EVENTS.CARE_PORTAL, { pet_name: petData?.name });
      getGeneralCare(petData.id);
    }
  }, [petData?.id]);

  const handleAccordianHeaderClick = (socData: [string, ISOCItemResponse[]]) => {
    switch (socData[0]) {
    case "Diagnostic":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_DIAGNOSTIC,
        { pet_name: petData.name, pet_overdue: petData.socStatus == "Overdue" ? "yes" : "no", wellness_subscriber: isWellnessPlan ? "yes" : "no" });
      break;
    case "Vaccination":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_VACCINATION,
        { pet_name: petData.name, pet_overdue: petData.socStatus == "Overdue" ? "yes" : "no", wellness_subscriber: isWellnessPlan ? "yes" : "no" });
      break;
    case "Preventative":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_PREVENTIVE,
        { pet_name: petData.name, pet_overdue: petData.socStatus == "Overdue" ? "yes" : "no", wellness_subscriber: isWellnessPlan ? "yes" : "no" });
      break;
    case "Dental":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_DENTAL,
        { pet_name: petData.name, pet_overdue: petData.socStatus == "Overdue" ? "yes" : "no", wellness_subscriber: isWellnessPlan ? "yes" : "no" });

      break;
    }
  };

  const handleAccordianSummaryClick = (socItems: ISOCItemResponse) => {
    switch (socItems.type) {
    case "Diagnostic":
      userActivityLogger(MIXPANEL_EVENTS.DIAGNOSTIC_ITEM_CLICK,
        {
          pet_name: petData.name, wellness_subscriber: isWellnessPlan ? "yes" : "no", due_status: socItems.status,
          diagnostic_type: socItems.name, last_fullfilled: socItems.lastGivenIso, next_due: socItems.nextDoseIso, fullfilled_by: socItems.vetName,

        });
      break;
    case "Vaccination":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_VACCINATION_CLICK,
        {
          pet_name: petData.name, wellness_subscriber: isWellnessPlan ? "yes" : "no", due_status: socItems.status,
          vaccination_type: socItems.name, last_fullfilled: socItems.lastGivenIso, next_due: socItems.nextDoseIso, fullfilled_by: socItems.vetName,
        });
      break;
    case "Preventative Medication":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_PREVENTIVE_CLICK,
        {
          "pet_name": petData.name, "wellness_subscriber": isWellnessPlan ? "yes" : "no", "due_status": socItems.status,
          "preventative/ medication_type": socItems.name, "last_fullfilled": socItems.lastGivenIso, "next_due": socItems.nextDoseIso, "fullfilled_by": socItems.vetName,
        });
      break;
    case "Dental":
      userActivityLogger(MIXPANEL_EVENTS.VISIT_DENTAL_CLICK,
        {
          "pet_name": petData.name, "wellness_subscriber": isWellnessPlan ? "yes" : "no", "due_status": socItems.status,
          "dental_procedure_type": socItems.name, "last_fullfilled": socItems.lastGivenIso, "next_due": socItems.nextDoseIso, "fullfilled_by": socItems.vetName,

        });

      break;
    }
  };

  useEffect(() => {
    if (petCare) {
      const currentPetData = petCare.pets.filter((pets: any) => pets?.id == petData.id);
      if (!currentPetData[0]) return;
      const isHavingWellnessPlan = currentPetData[0].items.wellnessPlanStatus;
      const isOverdue = currentPetData[0].socStatus;

      setIsWellnessPlan(isHavingWellnessPlan);
      userActivityLogger(MIXPANEL_EVENTS.CARE_SECTION,
        {
          referred_from_page: "care_portal", wellness_subscriber: isHavingWellnessPlan ? "yes" : "no",
          pet_overdue: isOverdue == "Overdue" ? "yes" : "no",
          pet_name: petData.name,
        });
    }
  }, [petCare]);

  const SOC_STATUS = ["Overdue", "Incomplete", "Almost Due"];

  useEffect(() => {
    const openAccordion = socMappedData.find((value: any) => {
      const overdueItem = value[1].find((item: any) => SOC_STATUS.includes(item.status));

      return overdueItem !== undefined;
    });

    const specificOverdueSoc = openAccordion ?
      {
        overdueItem: openAccordion[1].find((item: any) => SOC_STATUS.includes(item.status)),
        parentValue: openAccordion[0],
      } :
      null;

    setSpecificOverdueItem(specificOverdueSoc);
  }, [petData]);

  return (
    <div className="flex flex-col gap-3 h-full w-full">

      {reminderCard ?
        <SocReminderCard socData={socMappedData} petData={petData}
          socIcons={SOC_ICONS} handleReminder={handleReminder}/> :
        null}

      <Typography variant="h4" className="font-semibold">Standard of Care</Typography>

      {petData?.socStatus === "" ?
        <div className="h-full flex-grow flex flex-col justify-center items-center max-h-[50vh] 4xl:max-h-[35vh]">
          <NullGeneric
            image="/assets/icons/socNull.png"
            title="Your Pet's Care Roadmap Awaits"
            description="Schedule an appointment to see a detailed care plan for your pet,
           designed by our expert veterinarians."
            handleCTA={handleBookAppointment}
            isButtonDisabled={isBookingBlocked}
          /> </div>:
        socMappedData.map((value: any) => (
          <div key={value[0]}>
            {value[1].length ?
              <SOCAccordionCard isWellnessPlan={isWellnessPlan} handleAccordianHeaderClick={handleAccordianHeaderClick}
                handleAccordianSummaryClick={handleAccordianSummaryClick}
                socIcons={SOC_ICONS} socData={value} handleChange={handleChange} expanded={expanded} petData={petData}
                specificOverdueItem={specificOverdueItem} setSpecificOverdueItem={setSpecificOverdueItem}/> :
              null }
          </div>
        ))
      }
    </div>
  );
};

export default StandardOfCareContainer;
